// A discount entry is the number that was typed (10 for 10%, 50 for TK 50) and how it applies, so
// a reprint shows that value. `discountCost` stays the taka amount totals, returns and reports use.
// Mirrors `DiscountEntryType` in the API (salesCopy.enum.ts).
export enum EDiscountEntryType {
  Percent = 'PERCENT',
  // Flat on the line or the invoice: POS line discount, invoice discount.
  Amount = 'AMOUNT',
  // Multiplied by quantity: Unit Discount on the sales form.
  AmountPerUnit = 'AMOUNT_PER_UNIT',
}

export type DiscountEntry = {
  discountEntryValue?: number | null;
  discountEntryType?: EDiscountEntryType | null;
};

export const calculateSaleLineDiscountCost = (
  quantity: number,
  unitPrice: number,
  discountEntryValue: number | null | undefined,
  discountEntryType: EDiscountEntryType | null | undefined,
): number => {
  if (!discountEntryValue) return 0;

  switch (discountEntryType) {
    case EDiscountEntryType.Percent:
      return Number(((quantity * unitPrice * discountEntryValue) / 100).toFixed(2));
    case EDiscountEntryType.AmountPerUnit:
      return Number((discountEntryValue * quantity).toFixed(2));
    case EDiscountEntryType.Amount:
      return Number(discountEntryValue.toFixed(2));
    default:
      return 0;
  }
};

export const getSaleLineDiscountEntryError = (
  unitPrice: number,
  discountEntryValue: number | null | undefined,
  discountEntryType: EDiscountEntryType | null | undefined,
): string | null => {
  if (!discountEntryValue) return null;
  if (discountEntryType === EDiscountEntryType.Percent && discountEntryValue > 100) {
    return 'Percent discount cannot be higher than 100%';
  }
  if (discountEntryType === EDiscountEntryType.AmountPerUnit && discountEntryValue > unitPrice) {
    return 'Unit Discount cannot be higher than Unit Price';
  }
  return null;
};

// The Unit Discount entry to show when editing a line. A line that only has taka `discountCost`
// (a CSV import) becomes a per-unit taka entry, which is what Edit Product used to rescale.
export const getUnitDiscountEntryForEdit = (
  line: DiscountEntry & { quantity: number; discountCost?: number },
): Required<DiscountEntry> => {
  if (line.discountEntryValue && line.discountEntryType) {
    return {
      discountEntryValue: line.discountEntryValue,
      discountEntryType: line.discountEntryType,
    };
  }
  if (line.discountCost) {
    return {
      discountEntryValue: Number((line.discountCost / (line.quantity || 1)).toFixed(2)),
      discountEntryType: EDiscountEntryType.AmountPerUnit,
    };
  }
  return { discountEntryValue: null, discountEntryType: EDiscountEntryType.AmountPerUnit };
};

const formatDiscountNumber = (value: number) =>
  value.toLocaleString('en-US', { maximumFractionDigits: 2 });

// Prints the entry as typed: 10% or TK 50. A sale saved before entries existed prints its taka
// `discountCost`. No discount prints `emptyText`.
export const formatDiscountEntry = (
  discountEntryValue: number | null | undefined,
  discountEntryType: EDiscountEntryType | null | undefined,
  discountCost: number | null | undefined,
  emptyText = '',
): string => {
  if (discountEntryValue && discountEntryType) {
    return discountEntryType === EDiscountEntryType.Percent
      ? `${formatDiscountNumber(discountEntryValue)}%`
      : `TK ${formatDiscountNumber(discountEntryValue)}`;
  }
  if (discountCost) return `TK ${formatDiscountNumber(discountCost)}`;
  return emptyText;
};

const formatTakaWithPaisa = (value: number) =>
  `TK ${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// The small second line under a line discount: the other unit, plus taka each and in total, so
// 10% reads "TK 22.50 each · TK 67.50 total" and TK 100 per unit reads "3.57% · TK 300.00 total".
// A single unit skips the repetition. Built from the stored taka discountCost.
export const describeLineDiscountEquivalent = (
  quantity: number,
  unitPrice: number,
  discountCost: number | null | undefined,
  discountEntryValue: number | null | undefined,
  discountEntryType: EDiscountEntryType | null | undefined,
): string => {
  const lineTotal = quantity * unitPrice;
  if (!discountCost || !lineTotal || !quantity) return '';

  const percentOfLine = `${formatDiscountNumber((discountCost / lineTotal) * 100)}%`;
  const takaEach = formatTakaWithPaisa(discountCost / quantity);
  const takaTotal = formatTakaWithPaisa(discountCost);
  const hasEntry = !!discountEntryValue && !!discountEntryType;

  if (hasEntry && discountEntryType === EDiscountEntryType.Percent) {
    return quantity === 1 ? takaTotal : `${takaEach} each · ${takaTotal} total`;
  }
  if (hasEntry && discountEntryType === EDiscountEntryType.AmountPerUnit) {
    return quantity === 1 ? percentOfLine : `${percentOfLine} · ${takaTotal} total`;
  }
  // A flat taka amount on the line (POS), or an older sale's taka total.
  return quantity === 1 ? percentOfLine : `${percentOfLine} · ${takaEach} each`;
};

// The invoice or order discount. A percent also shows its taka amount, 10% (TK 645), because the
// sales form takes it of the subtotal before line discounts, so 10% of the shown subtotal would
// not match the total.
export const formatInvoiceDiscountEntry = (
  discountEntryValue: number | null | undefined,
  discountEntryType: EDiscountEntryType | null | undefined,
  discountCost: number | null | undefined,
): string => {
  const entryLabel = formatDiscountEntry(discountEntryValue, discountEntryType, discountCost, '0');
  if (discountEntryType === EDiscountEntryType.Percent && discountEntryValue && discountCost) {
    return `${entryLabel} (TK ${formatDiscountNumber(discountCost)})`;
  }
  return entryLabel;
};
